import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import {
  Camera, Loader2, CheckCircle2, AlertTriangle,
  Shield, Activity, Sparkles, Upload,
} from 'lucide-react';
import { LuminaService } from '../../../services/api';

export const ScheduleLensDialog = ({ open, onOpenChange, onAppointmentsExtracted }) => {
  const [step, setStep] = useState('upload');
  const [scanProgress, setScanProgress] = useState([]);
  const [extractedApts, setExtractedApts] = useState([]);
  const [scanError, setScanError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setStep('scanning');
    setScanProgress([{ name: file.name, type: file.type.includes('csv') ? 'CSV' : file.name.split('.').pop().toUpperCase(), size: `${Math.round(file.size / 1024)} KB`, status: 'scanning' }]);
    setScanError(null);

    try {
      // Simulate scanning visual then call API
      await new Promise(r => setTimeout(r, 600));
      setScanProgress(prev => prev.map(f => ({ ...f, status: 'done' })));
      setStep('extracting');

      const data = await LuminaService.scanSchedule(file);
      if (data.success && data.appointments && data.appointments.length > 0) {
        setExtractedApts(data.appointments);
        setStep('done');
      } else {
        setScanError('No appointments found in the uploaded file');
        setStep('error');
      }
    } catch (err) {
      setScanError(err.message || 'Schedule scan failed');
      setStep('error');
    }
  };

  const handleConfirm = () => {
    onAppointmentsExtracted(extractedApts);
    toast.success(`${extractedApts.length} appointments added`);
    handleClose();
  };
  const handleClose = () => { setStep('upload'); setScanProgress([]); setExtractedApts([]); setScanError(null); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="schedule-lens-dialog">
        <div className="bg-black px-5 py-3.5 flex items-center gap-3">
          <Camera className="h-4 w-4 text-white" />
          <DialogTitle className="text-sm font-bold text-white">Scan today's schedule</DialogTitle>
        </div>
        <div className="p-5">
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                className="flex flex-col items-center justify-center py-8 px-6 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="schedule-upload-zone"
              >
                <Camera className="h-8 w-8 text-slate-300 mb-3" />
                <p className="text-sm font-medium text-slate-600">Upload Schedule File</p>
                <p className="text-xs text-slate-400 mt-1 text-center">CSV appointment lists, PDF encounter summaries, or image schedules</p>
                <p className="text-[10px] text-violet-500 mt-2">Click to browse or drag & drop</p>
              </div>
              <input ref={fileInputRef} type="file" accept=".csv,.pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0])} data-testid="schedule-file-input" />
              <div className="space-y-2">
                <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-2.5 flex items-start gap-2">
                  <Shield className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-[10px] text-amber-700 leading-relaxed">Do not upload any personal information such as NPI, PHI, or other protected data.</p>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-2.5 flex items-start gap-2">
                  <Activity className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-[10px] text-blue-700 leading-relaxed">This is a trial site. Features on the live site will be more comprehensive and robust.</p>
                </div>
              </div>
            </div>
          )}
          {step === 'scanning' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1"><Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" /><p className="text-xs font-medium text-slate-600">Scanning documents...</p></div>
              {scanProgress.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 bg-white">
                  <div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center">
                    {f.status === 'done' ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
                  </div>
                  <div className="flex-1"><p className="text-xs font-medium text-slate-700">{f.name}</p><p className="text-[10px] text-slate-400">{f.type} &middot; {f.size}</p></div>
                  <Badge className={f.status === 'done' ? "bg-emerald-50 text-emerald-700 text-[9px] border-0" : "bg-slate-50 text-slate-500 text-[9px] border-0"}>
                    {f.status === 'done' ? 'Scanned' : 'Reading...'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
          {step === 'extracting' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1"><Sparkles className="h-3.5 w-3.5 text-slate-500 animate-pulse" /><p className="text-xs font-medium text-slate-600">AI extracting appointments...</p></div>
              <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 text-violet-500 animate-spin" /></div>
            </div>
          )}
          {step === 'done' && (
            <div className="space-y-4">
              <div className="text-center py-3">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-slate-800" data-testid="schedule-scan-count">{extractedApts.length} appointments found</p>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {extractedApts.map((apt, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100" data-testid={`extracted-apt-${i}`}>
                    <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">{apt.name.split(' ').map(n => n[0]).join('')}</div>
                    <div className="flex-1 min-w-0"><p className="text-xs font-medium text-slate-800">{apt.name}</p><p className="text-[10px] text-slate-500">{apt.time} &middot; {apt.chief || apt.type}</p></div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleConfirm} className="flex-1 bg-black hover:bg-gray-800 text-white text-xs gap-2 h-9" data-testid="schedule-lens-confirm-btn"><CheckCircle2 className="h-3.5 w-3.5" /> Add to Schedule</Button>
                <Button variant="outline" onClick={handleClose} className="text-xs h-9">Cancel</Button>
              </div>
            </div>
          )}
          {step === 'error' && (
            <div className="space-y-4" data-testid="schedule-scan-error">
              <div className="text-center py-4">
                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-slate-800">Schedule Scan Failed</p>
                <p className="text-xs text-slate-500 mt-1">{scanError}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setStep('upload'); setScanError(null); }} className="flex-1 bg-black hover:bg-gray-800 text-white text-xs gap-2 h-9"><Upload className="h-3.5 w-3.5" /> Try Again</Button>
                <Button variant="outline" onClick={handleClose} className="text-xs h-9">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
